import { Mount, SensorType, ProductSubCategoryEnum, ProductTypeEnum, ProductUsageEnum } from "@shared/enums";
import { MOUNTS, PRODUCT_TARGET_VALUES } from "@shared/values";
import { ProductDataResponseDTO } from "@shared/DTO";
import { ProductFilterTag, ProductListingItem, ProductListingSelections } from "../abProducts.types";
import { getListingCompatibleLensMounts } from "./listingMountCompatibility";
import {
  PRODUCT_SUB_CATEGORY_FILTER_WHITELIST, PRODUCT_USAGE_FILTER_WHITELIST, PRODUCT_USAGE_FILTER_MIN_SCORE,
} from "./productFilterConfig";

export const EMPTY_PRODUCT_LISTING_SELECTIONS: ProductListingSelections = Object.freeze({
  brands: [], productTypes: [], sensorFormats: [], compatibleLensMounts: [], targets: [], subCategories: [], usages: [],
});

export const getBrandOptions = (items: readonly ProductListingItem[]): string[] =>
  [...new Set(items.map(({ product }) => product.productData?.brand).filter((brand): brand is string => !!brand))]
    .sort((a, b) => a.localeCompare(b, "it"));

export const getProductTypeOptions = (items: readonly ProductListingItem[]): ProductTypeEnum[] => {
  const available = new Set(items.map(({ product }) => product.productData?.productType));
  return Object.values(ProductTypeEnum).filter((value) => available.has(value));
};

export const getSensorFormatOptions = (items: readonly ProductListingItem[]): SensorType[] => {
  const available = new Set(items.map(({ product }) => product.cameraData?.sensor?.sensorType));
  return Object.values(SensorType).filter((value) => available.has(value));
};

export const getTargetOptions = (items: readonly ProductListingItem[]): number[] => {
  const available = new Set(items.map(({ product }) => product.productData?.target));
  return PRODUCT_TARGET_VALUES.map((_, index) => index + 1).filter((value) => available.has(value));
};

export const getSubCategoryOptions = (items: readonly ProductListingItem[]): ProductSubCategoryEnum[] => {
  const available = new Set(items.flatMap(({ product }) => product.productData?.subCategory ?? []));
  return Object.values(ProductSubCategoryEnum).filter((value) =>
    PRODUCT_SUB_CATEGORY_FILTER_WHITELIST.includes(value) && available.has(value));
};

const getEligibleUsages = (data: ProductDataResponseDTO): ProductUsageEnum[] =>
  (data?.usage ?? []).filter(({ key, score }) =>
    score >= PRODUCT_USAGE_FILTER_MIN_SCORE && PRODUCT_USAGE_FILTER_WHITELIST.includes(key)).map(({ key }) => key);

export const getUsageOptions = (items: readonly ProductListingItem[]): ProductUsageEnum[] => {
  const available = new Set(items.flatMap(({ product }) => getEligibleUsages(product.productData)));
  return Object.values(ProductUsageEnum).filter((value) => available.has(value));
};

export const getLensMountOptions = (items: readonly ProductListingItem[]): Mount[] => {
  const available = new Set(items.flatMap(({ purchaseOptions }) => purchaseOptions.type === "lensMounts"
    ? purchaseOptions.options.map(({ mount }) => mount.public_id) : []));
  return MOUNTS.filter(({ public_id }) => getListingCompatibleLensMounts([public_id])
    .some((compatible) => available.has(compatible))).map(({ public_id }) => public_id);
};

export const filterProductListingItems = (
  items: readonly ProductListingItem[],
  enabled: readonly ProductFilterTag[],
  selections: ProductListingSelections,
): ProductListingItem[] => items.flatMap((item) => {
  if (enabled.includes("ab-filter-brand") && selections.brands.length
    && !selections.brands.includes(item.product.productData?.brand)) return [];
  if (enabled.includes("ab-filter-product-type") && selections.productTypes.length
    && !selections.productTypes.includes(item.product.productData?.productType)) return [];
  if (enabled.includes("ab-filter-sensor-format") && selections.sensorFormats.length
    && !selections.sensorFormats.includes(item.product.cameraData?.sensor?.sensorType)) return [];
  if (enabled.includes("ab-filter-target") && selections.targets.length
    && !selections.targets.includes(item.product.productData?.target)) return [];
  if (enabled.includes("ab-filter-sub-category") && selections.subCategories.length
    && !item.product.productData?.subCategory?.some((value) =>
      PRODUCT_SUB_CATEGORY_FILTER_WHITELIST.includes(value) && selections.subCategories.includes(value))) return [];
  if (enabled.includes("ab-filter-usage") && selections.usages.length
    && !getEligibleUsages(item.product.productData).some((value) => selections.usages.includes(value))) return [];
  if (!enabled.includes("ab-filter-lens-compatibility") || !selections.compatibleLensMounts.length
    || item.purchaseOptions.type === "default") return [item];
  const allowed = new Set(selections.compatibleLensMounts);
  const options = item.purchaseOptions.options.filter(({ mount }) => allowed.has(mount.public_id));
  return options.length ? [{ ...item, purchaseOptions: { type: "lensMounts" as const, options } }] : [];
});

export const pruneProductListingSelections = (
  items: readonly ProductListingItem[],
  enabled: readonly ProductFilterTag[],
  values: ProductListingSelections,
): ProductListingSelections => {
  const brands = new Set(getBrandOptions(items));
  const productTypes = new Set(getProductTypeOptions(items));
  const formats = new Set(getSensorFormatOptions(items));
  const targets = new Set(getTargetOptions(items));
  const subCategories = new Set(getSubCategoryOptions(items));
  const usages = new Set(getUsageOptions(items));
  return {
    brands: enabled.includes("ab-filter-brand") ? values.brands.filter((value) => brands.has(value)) : [],
    productTypes: enabled.includes("ab-filter-product-type") ? values.productTypes.filter((value) => productTypes.has(value)) : [],
    sensorFormats: enabled.includes("ab-filter-sensor-format") ? values.sensorFormats.filter((value) => formats.has(value)) : [],
    compatibleLensMounts: enabled.includes("ab-filter-lens-compatibility") ? values.compatibleLensMounts : [],
    targets: enabled.includes("ab-filter-target") ? values.targets.filter((value) => targets.has(value)) : [],
    subCategories: enabled.includes("ab-filter-sub-category") ? values.subCategories.filter((value) => subCategories.has(value)) : [],
    usages: enabled.includes("ab-filter-usage") ? values.usages.filter((value) => usages.has(value)) : [],
  };
};
